import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useSessionStore from '../store/sessionStore'
import { generateKey, encryptBlob, exportKeyToBase64, ivToBase64, buildTransferUrl } from '../utils/crypto'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import Badge from '../components/ui/Badge'
import styles from './Finish.module.css'

const FILE_ICONS = {
  id: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="12" cy="10" r="2" />
      <path d="M8 16c0-2 2-3 4-3s4 1 4 3" />
    </svg>
  ),
  selfie: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
    </svg>
  ),
  video: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  ),
}

const FILE_ICON_CLASS = {
  id: styles.fileIconId,
  selfie: styles.fileIconSelfie,
  video: styles.fileIconVideo,
}

export default function Finish() {
  const navigate = useNavigate()
  const {
    capturedFiles,
    verifLink,
    updateCapturedFile,
    removeCapturedFile,
    setSelectedType,
    setSelectedMethod,
    sessionId,
  } = useSessionStore()
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState(null)

  const allUploaded = capturedFiles.every((f) => f.status === 'done')

  const handlePrepareAnother = () => {
    navigate('/select-type')
  }

  const handlePrepareFirst = () => {
    navigate('/select-type')
  }

  const handleDelete = (id) => {
    removeCapturedFile(id)
  }

  const handleReplace = (file) => {
    setSelectedType(file.type)
    setSelectedMethod(file.method)
    navigate('/capture', { state: { replaceId: file.id } })
  }

  const handleDoneConnectPhone = async () => {
    if (allUploaded) {
      navigate('/connect')
      return
    }

    setIsUploading(true)
    setError(null)

    try {
      for (const file of capturedFiles) {
        if (file.status === 'done') continue

        const key = await generateKey()
        const { encryptedBuffer, iv } = await encryptBlob(file.processedBlob, key)
        const keyB64 = await exportKeyToBase64(key)
        const ivB64 = ivToBase64(iv)

        const blob = new Blob([encryptedBuffer])
        const formData = new FormData()
        formData.append('file', blob)
        formData.append('iv', ivB64)
        formData.append('mimeType', file.mimeType)
        formData.append('sessionId', sessionId || crypto.randomUUID())

        const res = await fetch('https://verifylink-api.ryanhexrivk.workers.dev/api/upload', { method: 'POST', body: formData })
        if (!res.ok) {
          const data = await res.json().catch(() => ({}))
          throw new Error(data.error || `Upload failed for ${file.originalName}. Please try again.`)
        }
        const data = await res.json()

        const url = buildTransferUrl(
          window.location.origin,
          data.fileId,
          keyB64,
          ivB64,
          file.mimeType
        )

        updateCapturedFile(file.id, {
          status: 'done',
          fileId: data.fileId,
          transferUrl: url,
        })
      }

      navigate('/connect')
    } catch (err) {
      setError(err.message || 'Something went wrong during upload. Please try again.')
    } finally {
      setIsUploading(false)
    }
  }

  const getTypeLabel = (type) => {
    switch (type) {
      case 'id': return 'ID Document'
      case 'selfie': return 'Selfie Photo'
      case 'video': return 'Verification Video'
      default: return 'File'
    }
  }

  if (capturedFiles.length === 0) {
    return (
      <main className={styles.main}>
        <div className={styles.container}>
          <h1 className={styles.title}>No files prepared yet</h1>
          <p className={styles.sub}>Get started by preparing your first verification file.</p>
          <Button onClick={handlePrepareFirst} size="lg">
            Prepare a file
          </Button>
        </div>
      </main>
    )
  }

  return (
    <main className={styles.main}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>Files prepared</h1>
          <p className={styles.sub}>
            {capturedFiles.length} file{capturedFiles.length > 1 ? 's' : ''} ready to transfer.
          </p>
        </div>

        <div className={styles.fileList}>
          {capturedFiles.map((file) => (
            <Card key={file.id} padding="md" className={styles.fileCard}>
              <div className={styles.fileRow}>
                <div className={styles.fileInfo}>
                  <div className={[styles.fileIcon, FILE_ICON_CLASS[file.type] || styles.fileIconId].join(' ')}>
                    {FILE_ICONS[file.type] || FILE_ICONS.id}
                  </div>
                  <div className={styles.fileNameWrap}>
                    <span className={styles.fileName}>{file.originalName}</span>
                    <span className={styles.fileMeta}>
                      {getTypeLabel(file.type)} · {(file.sizeBytes / 1024 / 1024).toFixed(2)} MB
                    </span>
                  </div>
                </div>
                <div className={styles.fileActions}>
                  {file.status === 'done' ? (
                    <Badge variant="success">Uploaded</Badge>
                  ) : (
                    <Badge>Ready</Badge>
                  )}
                  <button
                    className={styles.actionBtn}
                    onClick={() => handleReplace(file)}
                    aria-label={`Replace ${file.originalName}`}
                    title="Replace"
                  >
                    Replace
                  </button>
                  <button
                    className={styles.deleteBtn}
                    onClick={() => handleDelete(file.id)}
                    aria-label={`Delete ${file.originalName}`}
                    title="Delete"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {verifLink && (
          <Card padding="md" className={styles.linkCard}>
            <p className={styles.linkLabel}>Your verification link</p>
            <a href={verifLink} target="_blank" rel="noopener noreferrer" className={styles.linkValue}>
              {verifLink}
            </a>
          </Card>
        )}

        {error && (
          <div className={styles.errorBox} role="alert">
            <p>{error}</p>
          </div>
        )}

        {isUploading && (
          <div className={styles.uploadingOverlay}>
            <div className={styles.spinner} aria-hidden="true" />
            <p className={styles.uploadingText}>Encrypting and uploading your files…</p>
          </div>
        )}

        {!isUploading && (
          <div className={styles.actions}>
            <Button variant="ghost" onClick={handlePrepareAnother}>
              Prepare another file
            </Button>
            <Button onClick={handleDoneConnectPhone} size="lg">
              {allUploaded ? 'Connect phone' : 'Done, connect phone'}
            </Button>
          </div>
        )}
      </div>
    </main>
  )
}
